import React, { useRef, useState } from "react";
import ReglamentoTemplate from "../../components/reglamento/ReglamentoTemplate";
import ReglamentoForm from "../../components/reglamento/ReglamentoForm";
import { defaultConfig } from "../../utils/reglamentoConfig";
import { generarPresupuestoPdf } from "../../utils/presupuestoPdf";
import { useBuilding } from "../../context/BuildingContext";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import { useToast } from "../../components/Toast";
import { colors } from "../../utils/designTokens";

// Pantalla de generacion de Reglamento Interno (admin / superadmin).
// Layout split: preview del documento a la izquierda, formulario controlado a la derecha.
// El estado vive aca; los cambios del form actualizan el preview en vivo.
// "Descargar PDF" reutiliza la funcion generica de presupuestoPdf.js (captura .page de las hojas activas).
// C1 — boton "Generar con IA" llama al backend y mergea el resultado al config.

export default function Reglamento() {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [config, setConfig] = useState(() => defaultConfig());
  const [generating, setGenerating] = useState(false);
  const [generatingIA, setGeneratingIA] = useState(false);
  const [iaNotas, setIaNotas] = useState(null);   // notas_revision del backend
  const [iaNarrativas, setIaNarrativas] = useState(null);  // narrativaCapitulos
  const templateRef = useRef(null);

  const handleGenerar = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const safeNombre = (config.edificio || "Reglamento").replace(/[^\w\sáéíóúÁÉÍÓÚñÑ-]/g, "").trim();
      const filename = `Reglamento Interno - ${safeNombre}.pdf`;
      await generarPresupuestoPdf(templateRef.current, filename, { download: true });
    } catch (err) {
      console.error("Error generando PDF:", err);
      alert("No se pudo generar el PDF. Revisá la consola para detalles.");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerarConIA = async () => {
    if (generatingIA) return;
    if (!selectedBuilding) {
      showToast("Seleccioná un edificio antes de generar con IA.", "warning");
      return;
    }
    setGeneratingIA(true);
    setIaNotas(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/generate-regulation`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(selectedBuilding.buildingid),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo generar el reglamento con IA.", "error");
        return;
      }
      const data = await resp.json();
      // Merge al config existente — mantenemos los datos del edificio y mergeamos
      // solo lo que la IA generó.
      setConfig((prev) => ({
        ...prev,
        edificio: data.building_name || prev.edificio,
        capitulosVisibles: { ...prev.capitulosVisibles, ...(data.capitulosVisibles || {}) },
        prohibiciones: data.prohibiciones?.length ? data.prohibiciones : prev.prohibiciones,
        sanciones: data.sanciones?.length ? data.sanciones : prev.sanciones,
        horarios: {
          ...prev.horarios,
          ...(data.horarios || {}),
        },
      }));
      setIaNotas(data.notas_revision || []);
      setIaNarrativas(data.narrativaCapitulos || {});
      showToast("Reglamento generado por IA. Revisá antes de exportar.", "success");
    } catch (e) {
      console.error("[C1] error:", e);
      showToast(e.message || "Error inesperado generando con IA.", "error");
    } finally {
      setGeneratingIA(false);
    }
  };

  return (
    <div style={S.container}>
      <main style={S.previewArea}>
        <div style={S.toolbar}>
          <button
            type="button"
            onClick={handleGenerarConIA}
            disabled={generatingIA || !selectedBuilding}
            style={{
              ...S.iaButton,
              opacity: generatingIA || !selectedBuilding ? 0.6 : 1,
              cursor: generatingIA || !selectedBuilding ? "not-allowed" : "pointer",
            }}
            title={!selectedBuilding ? "Seleccioná un edificio primero" : "Generar reglamento adaptado al edificio"}
          >
            {generatingIA ? "Generando con IA…" : "🤖 Generar con IA"}
          </button>
          {iaNotas && iaNotas.length > 0 && (
            <details style={S.notasBox}>
              <summary style={S.notasSummary}>⚠ {iaNotas.length} nota{iaNotas.length > 1 ? "s" : ""} de revisión IA</summary>
              <ul style={S.notasList}>
                {iaNotas.map((n, i) => <li key={i} style={S.notasItem}>{n}</li>)}
              </ul>
            </details>
          )}
        </div>
        <div style={S.previewInner}>
          <ReglamentoTemplate ref={templateRef} config={config} />
        </div>
      </main>
      <ReglamentoForm
        config={config}
        onChange={setConfig}
        onGenerarPdf={handleGenerar}
        generating={generating}
      />
    </div>
  );
}

const S = {
  container: {
    display: "flex",
    height: "100%",
    minHeight: 0,
    background: "#E9EBEE",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
  },
  previewArea: {
    flex: 1,
    minWidth: 0,
    overflow: "auto",
  },
  previewInner: {
    minWidth: "fit-content",
  },
  toolbar: {
    padding: "12px 16px",
    background: "#fff",
    borderBottom: "1px solid #E9EBEE",
    display: "flex",
    alignItems: "center",
    gap: 12,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  iaButton: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 400,
    color: "#fff",
    background: "#0080ff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  notasBox: {
    flex: 1,
    background: "#fff8e1",
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: 12,
    color: "#7c5800",
  },
  notasSummary: {
    cursor: "pointer",
    fontWeight: 400,
  },
  notasList: {
    margin: "6px 0 0",
    paddingLeft: 20,
  },
  notasItem: {
    margin: "4px 0",
  },
};
